import { faker } from '@faker-js/faker'
import type { Review } from '../../types/review'

const REVIEW_TITLES_POSITIVE = [
  'Excellent quality!',
  'Highly recommended',
  'Great product',
  'Very satisfied',
  'Worth the money',
  'Fresh and tasty',
  'Perfect condition',
  'Will buy again',
  'Amazing quality',
  'Best purchase ever',
]

const REVIEW_TITLES_NEUTRAL = [
  'It\'s okay',
  'Average product',
  'Could be better',
  'Decent quality',
  'Not bad',
]

const REVIEW_TITLES_NEGATIVE = [
  'Disappointed',
  'Not as expected',
  'Poor quality',
  'Would not recommend',
  'Not worth it',
]

const REVIEW_COMMENTS_POSITIVE = [
  'The product quality exceeded my expectations. Fresh and well-packaged. Will definitely order again!',
  'Absolutely love this product! Delivery was on time and everything was in perfect condition.',
  'Very happy with this purchase. The product is exactly as described and tastes great.',
  'Excellent quality and fair price. BotTola never disappoints!',
  'Fresh product, good packaging. Highly recommend to everyone.',
  'This is now my go-to product. Quality is consistently good every time I order.',
  'Great value for money. The product arrived fresh and the packaging was secure.',
  'I\'ve ordered this multiple times now and it\'s always fresh. Very reliable!',
]

const REVIEW_COMMENTS_NEUTRAL = [
  'The product is decent but nothing special. Packaging could be improved.',
  'It\'s an okay product. Does the job but I expected better quality for the price.',
  'Average quality. Not bad but not outstanding either.',
  'The product is fine but delivery took longer than expected.',
]

const REVIEW_COMMENTS_NEGATIVE = [
  'Product quality was not as good as I expected. Some items were not fresh.',
  'Disappointed with the quality. The packaging was damaged and one item was missing.',
  'Not worth the price. Quality is below average.',
  'The product arrived in poor condition. Would not recommend.',
]

export const generateMockReview = (productId: string, overrides?: Partial<Review>): Review => {
  const rating = faker.number.int({ min: 1, max: 5 })
  
  let title: string
  let comment: string
  
  if (rating >= 4) {
    title = (faker.helpers as any).arrayElement(REVIEW_TITLES_POSITIVE)
    comment = (faker.helpers as any).arrayElement(REVIEW_COMMENTS_POSITIVE)
  } else if (rating === 3) {
    title = (faker.helpers as any).arrayElement(REVIEW_TITLES_NEUTRAL)
    comment = (faker.helpers as any).arrayElement(REVIEW_COMMENTS_NEUTRAL)
  } else {
    title = (faker.helpers as any).arrayElement(REVIEW_TITLES_NEGATIVE)
    comment = (faker.helpers as any).arrayElement(REVIEW_COMMENTS_NEGATIVE)
  }

  const hasImages = rating >= 4 && faker.datatype.boolean({ probability: 0.3 })
  const images = hasImages
    ? Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () =>
        faker.image.url({ width: 400, height: 400 })
      )
    : []

  return {
    id: faker.string.uuid(),
    product_id: productId,
    user_id: faker.string.uuid(),
    user_name: faker.person.firstName() + ' ' + faker.person.lastName().charAt(0) + '.',
    user_avatar: faker.datatype.boolean({ probability: 0.6 }) 
      ? `https://ui-avatars.com/api/?name=${faker.person.firstName()}&background=random`
      : undefined,
    rating,
    title,
    comment,
    images,
    is_verified_purchase: faker.datatype.boolean({ probability: 0.8 }),
    helpful_count: faker.number.int({ min: 0, max: 50 }),
    helpful_voters: [],
    created_at: faker.date.recent({ days: 180 }).toISOString(),
    updated_at: faker.date.recent({ days: 180 }).toISOString(),
    ...overrides,
  }
}

export const generateMockReviews = (productId: string, count: number = 10): Review[] => {
  return Array.from({ length: count }, () => generateMockReview(productId))
}
